import { useIsAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-admin/index.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { BACKLOG } from '../../model/constants.tsx';
import { useBacklogSelector } from '../../state/index.tsx';
import { getOrderedCardIdsByCardListId } from '../../state/selectors/cards/index.tsx';
import {
	getHasConfluenceAccess,
	getHasConfluenceSpaces,
} from '../../state/selectors/whiteboards-entrypoint-experiment/index.tsx';

// Checks if the number of issues in the backlog is greater than 2
export const useIsBacklogCardListSizeGreaterThanTwo = (): boolean =>
	useBacklogSelector(getOrderedCardIdsByCardListId)[BACKLOG]?.length > 2;

// Checks it the tenant has Confluence site
const useTenantHasConfluence = (): boolean => {
	const { licensedProducts } = useTenantContext();
	return licensedProducts?.confluence === true;
};

const useTenantHasProductSuggestionsEnabled = (): boolean => {
	const { areProductSuggestionsEnabled } = useTenantContext();
	return areProductSuggestionsEnabled;
};

// Crossflow eligibile if the tenant does not have Confluence site and the user is an admin
export const useIsCrossFlowEligible = (): boolean => {
	const isAdmin = useIsAdmin();
	const hasConfluence = useTenantHasConfluence();
	return useTenantHasProductSuggestionsEnabled() && !hasConfluence && isAdmin;
};

export const useIsCrossFlowEligibleAllJiras = (): boolean => {
	const isAdmin = useIsAdmin();
	const hasConfluence = useTenantHasConfluence();
	const crossFlowEligible = useTenantHasProductSuggestionsEnabled() && !hasConfluence && isAdmin;
	const backlogCardListSizeGreaterThanTwo = useIsBacklogCardListSizeGreaterThanTwo();
	return crossFlowEligible && backlogCardListSizeGreaterThanTwo;
};

// Co-use eligible if the tenant has Confluence site and the user has access to that Confluence site
export const useIsCoUseEligible = (): boolean => {
	const hasAccess = useBacklogSelector(getHasConfluenceAccess);
	const hasSpaces = useBacklogSelector(getHasConfluenceSpaces);
	return useTenantHasConfluence() && hasAccess && hasSpaces;
};

export const useIsCoUseEligibleAllJiras = (): boolean => {
	const hasAccess = useBacklogSelector(getHasConfluenceAccess);
	const hasSpaces = useBacklogSelector(getHasConfluenceSpaces);
	const coUseEligible = useTenantHasConfluence() && hasAccess && hasSpaces;
	const backlogCardListSizeGreaterThanTwo = useIsBacklogCardListSizeGreaterThanTwo();
	return coUseEligible && backlogCardListSizeGreaterThanTwo;
};
